import type { Account, PlatformAccount, PublishSummary, Rule } from "@/Connect/types";
import { API_ROUTES } from "@/constants";
import { getIdToken } from "@/services/Auth";
import { getBaseApiUrl, getDefaultHeaders, request } from "@/utils/CreativeIntelligenceApi";

type AccountApiResponse = {
    accountsRules: [
        {
            rules: Rule[];
            platformAccount: PlatformAccount;
        }
    ];
    latestVersionId: string;
};

export async function getAccounts({ campaignId }: { campaignId: string }) {
    const baseUrl = getBaseApiUrl();
    const url = `${baseUrl}${API_ROUTES.publishCampaignAccounts(campaignId)}`;
    const token = await getIdToken();
    const headers = getDefaultHeaders(token);

    return request<AccountApiResponse>(url, headers);
}

export async function getCampaignPublishSummary({ campaignId }: { campaignId: string }) {
    const baseUrl = getBaseApiUrl();
    const url = `${baseUrl}${API_ROUTES.publishRulesCampaignsStatus(campaignId)}`;
    const token = await getIdToken();
    const headers = getDefaultHeaders(token);

    return request<PublishSummary[]>(url, headers);
}

type AccountPublishDetailsApiResponse = {
    error: {
        message: string;
    };
};

export async function getAccountPublishDetails({
    campaignId,
    platformAccountId
}: {
    campaignId: string;
    platformAccountId: number;
}) {
    const baseUrl = getBaseApiUrl();
    const url = `${baseUrl}${API_ROUTES.publishRulesCampaignsAccountsUpdates(campaignId, platformAccountId)}`;
    const token = await getIdToken();
    const headers = getDefaultHeaders(token);

    return request<AccountPublishDetailsApiResponse[]>(url, headers);
}

interface ValidateCampaignApiResponse {
    fileLocation: {
        bucket: string;
        key: string;
    };
}

export async function validateCampaign({
    campaignId,
    campaignData
}: {
    campaignId: string;
    campaignData: {
        latestVersionId: string;
        accountsRules: Account[];
        fileUrl: string;
    };
}) {
    const baseUrl = getBaseApiUrl();
    const url = `${baseUrl}${API_ROUTES.publishCampaignValidate(campaignId)}`;
    const token = await getIdToken();
    const headers = getDefaultHeaders(token);

    return request<ValidateCampaignApiResponse, typeof campaignData>(url, headers, "POST", campaignData);
}

export async function saveCampaign({
    campaignId,
    campaignData
}: {
    campaignId: string;
    campaignData: ValidateCampaignApiResponse & { latestVersionId: string };
}) {
    const baseUrl = getBaseApiUrl();
    const url = `${baseUrl}${API_ROUTES.publishCampaignSave(campaignId)}`;
    const token = await getIdToken();
    const headers = getDefaultHeaders(token);

    return request<typeof campaignData, { latestVersionId: string }>(url, headers, "POST", campaignData);
}

export async function getDesiredState({ campaignId }: { campaignId: string }) {
    const baseUrl = getBaseApiUrl();
    const url = `${baseUrl}${API_ROUTES.PUBLISH_RULES_DESIRED_STATES}`;
    const token = await getIdToken();
    const headers = getDefaultHeaders(token);

    return request<{ bucket: string; key: string }[], { campaignId: string }>(url, headers, "POST", { campaignId });
}

export async function publishCampaign({ campaignId }: { campaignId: string }) {
    const baseUrl = getBaseApiUrl();
    const url = `${baseUrl}${API_ROUTES.PUBLISH_RULES_PLATFORMS_PUBLISH}`;
    const token = await getIdToken();
    const headers = getDefaultHeaders(token);

    return request<
        { publishStatuses: { campaignId: string; status: "publishing"; account: PlatformAccount }[] },
        { campaignId: string }
    >(url, headers, "POST", { campaignId });
}

export async function getLocationSuggestions({ locationQuery }: { locationQuery: string }) {
    const baseUrl = getBaseApiUrl();
    const url = `${baseUrl}${API_ROUTES.OPENWEATHER_SEARCH_LOCATIONS}?key=${locationQuery}`;
    const token = await getIdToken();
    const headers = getDefaultHeaders(token);

    return request<{ name: string; country: string }[]>(url, headers);
}

export type InstagramAccountData = {
    id: string;
    username: string;
    name: string;
};

export async function getInstagramAccount({ campaignId }: { campaignId: string }) {
    const baseUrl = getBaseApiUrl();
    const url = `${baseUrl}${API_ROUTES.PUBFB_INSTAGRAM_USER}/${campaignId}`;
    const token = await getIdToken();
    const headers = getDefaultHeaders(token);

    return request<InstagramAccountData>(url, headers);
}
